
import { Company, User, Lead, UserRole, SubscriptionPlan, CallStat } from './types';

/** Paliers de revenu par défaut */
export const DEFAULT_REVENUE_BRACKETS = ['<5K', '5-20K', '20-50K', '50-100K', '>100K'];

/** Couleurs par défaut pour les paliers standards (utilisées si aucune config entreprise) */
export const DEFAULT_REVENUE_COLORS: Record<string, string> = {
  '<5K':     '#94a3b8',
  '5-20K':   '#60a5fa',
  '20-50K':  '#34d399',
  '50-100K': '#fb923c',
  '>100K':   '#f472b6',
};

/** Palette de couleurs pour les paliers de revenu potentiel */
export const REVENUE_COLOR_PALETTE: { hex: string; label: string }[] = [
  { hex: '#94a3b8', label: 'Gris' },
  { hex: '#60a5fa', label: 'Bleu' },
  { hex: '#22d3ee', label: 'Cyan' },
  { hex: '#34d399', label: 'Émeraude' },
  { hex: '#4ade80', label: 'Vert' },
  { hex: '#facc15', label: 'Jaune' },
  { hex: '#fb923c', label: 'Orange' },
  { hex: '#f87171', label: 'Rouge' },
  { hex: '#c084fc', label: 'Violet' },
  { hex: '#f472b6', label: 'Rose' },
];

/** Palette de couleurs sombres pour le fond des cartes prospect (mode dark) */
export const CARD_BG_COLOR_PALETTE_DARK: { hex: string; label: string }[] = [
  { hex: '#0e2244', label: 'Bleu nuit (défaut)' },
  { hex: '#1e1b4b', label: 'Indigo profond' },
  { hex: '#172554', label: 'Bleu marine' },
  { hex: '#0c4a6e', label: 'Bleu ciel foncé' },
  { hex: '#064e3b', label: 'Émeraude foncé' },
  { hex: '#1a2e05', label: 'Vert forêt' },
  { hex: '#422006', label: 'Marron café' },
  { hex: '#450a0a', label: 'Rouge bordeaux' },
  { hex: '#3b0764', label: 'Violet profond' },
  { hex: '#1e293b', label: 'Ardoise' },
  { hex: '#18181b', label: 'Zinc sombre' },
  { hex: '#0f172a', label: 'Slate très foncé' },
];

/** Palette de couleurs pour le fond des cartes prospect (mode light) */
export const CARD_BG_COLOR_PALETTE_LIGHT: { hex: string; label: string }[] = [
  { hex: '#ffffff', label: 'Blanc (défaut)' },
  { hex: '#94a3b8', label: 'Gris ardoise' },
  { hex: '#818cf8', label: 'Indigo' },
  { hex: '#60a5fa', label: 'Bleu' },
  { hex: '#22d3ee', label: 'Cyan' },
  { hex: '#34d399', label: 'Émeraude' },
  { hex: '#4ade80', label: 'Vert' },
  { hex: '#facc15', label: 'Jaune' },
  { hex: '#fb923c', label: 'Orange' },
  { hex: '#f87171', label: 'Rouge' },
  { hex: '#a78bfa', label: 'Violet' },
  { hex: '#e879f9', label: 'Fuchsia' },
];

export const MOCK_COMPANIES: Company[] = [
  { id: 'c1', name: 'TechFlow Solutions', plan: SubscriptionPlan.PRO, userLimit: 10, activeLeads: 145, status: 'active', createdAt: '2023-10-01' },
  { id: 'c2', name: 'Global Logistics Corp', plan: SubscriptionPlan.ENTERPRISE, userLimit: 50, activeLeads: 890, status: 'active', createdAt: '2023-11-15' },
  { id: 'c3', name: 'Solaris Energy', plan: SubscriptionPlan.BASIC, userLimit: 3, activeLeads: 42, status: 'suspended', createdAt: '2024-01-10' },
];

// Ajout de la propriété password (optionnelle dans l'interface mais utilisée ici pour le mock)
export const MOCK_USERS: (User & { password?: string })[] = [
  { id: 'u1', companyId: 'system', name: 'Super Admin', email: 'admin@nexus.com', role: UserRole.SUPER_ADMIN, password: '123' },
  { id: 'u2', companyId: 'c1', name: 'Jean Dupont', email: 'jean@techflow.com', role: UserRole.COMPANY_ADMIN, password: '123' },
  { id: 'u3', companyId: 'c1', name: 'Alice Martin', email: 'alice@techflow.com', role: UserRole.USER, password: '123' },
  { id: 'u4', companyId: 'c2', name: 'Robert Smith', email: 'robert@global.com', role: UserRole.COMPANY_ADMIN, password: '123' },
  { id: 'u5', companyId: 'c1', name: 'Marc Lefebvre', email: 'marc@techflow.com', role: UserRole.USER, password: '123' },
];

export const MOCK_LEADS: Lead[] = [
  { id: 'l1', companyId: 'c1', name: 'Paul Bernard', email: 'paul@gmail.com', phone: '+33612345678', status: 'new', assignedTo: 'u3', createdAt: '2024-03-15' },
  { id: 'l2', companyId: 'c1', name: 'Sophie Petit', email: 'sophie@outlook.fr', phone: '+33788990011', status: 'qualified', assignedTo: 'u3', createdAt: '2024-03-14' },
  { id: 'l3', companyId: 'c2', name: 'John Doe', email: 'john.doe@corp.com', phone: '+15551234', status: 'contacted', assignedTo: 'u4', createdAt: '2024-03-10' },
  { id: 'l4', companyId: 'c1', name: 'Marie Curie', email: 'marie.c@science.fr', phone: '+33611223344', status: 'won', assignedTo: 'u5', createdAt: '2024-03-12' },
  { id: 'l5', companyId: 'c1', name: 'Lucas Morel', email: 'lucas.m@gmail.com', phone: '+33655667788', status: 'new', assignedTo: 'u3', createdAt: '2024-03-16' },
  { id: 'l6', companyId: 'c1', name: 'Emma Durand', email: 'emma.d@yahoo.fr', phone: '+33710203040', status: 'contacted', assignedTo: 'u5', createdAt: '2024-03-16' },
  { id: 'l7', companyId: 'c1', name: 'Thomas Dubois', email: 't.dubois@hot.com', phone: '+33699887766', status: 'qualified', assignedTo: 'u3', createdAt: '2024-03-01' },
  { id: 'l8', companyId: 'c1', name: 'Chloe Simon', email: 'chloe.s@gmail.com', phone: '+33644332211', status: 'lost', assignedTo: 'u5', createdAt: '2024-03-02' },
  { id: 'l9', companyId: 'c1', name: 'Nicolas Leroy', email: 'n.leroy@tech.com', phone: '+33755443322', status: 'new', assignedTo: 'u3', createdAt: '2024-03-18' },
  { id: 'l10', companyId: 'c1', name: 'Julie Michel', email: 'j.michel@orange.fr', phone: '+33622110099', status: 'won', assignedTo: 'u3', createdAt: '2024-03-19' },
  { id: 'l11', companyId: 'c1', name: 'Antoine Roux', email: 'antoine.roux@proton.me', phone: '+33677889900', status: 'new', assignedTo: 'u5', createdAt: '2024-03-19' },
  { id: 'l12', companyId: 'c1', name: 'Camille David', email: 'c.david@gmail.com', phone: '+33700112233', status: 'contacted', assignedTo: 'u5', createdAt: '2024-03-20' },
  { id: 'l13', companyId: 'c1', name: 'Benoit Mercier', email: 'b.mercier@outlook.com', phone: '+33688776655', status: 'qualified', assignedTo: 'u3', createdAt: '2024-03-20' },
  { id: 'l14', companyId: 'c1', name: 'Isabelle Leroy', email: 'isabelle.l@pro.com', phone: '+33610203040', status: 'new', assignedTo: 'u3', createdAt: '2024-03-21' },
  { id: 'l15', companyId: 'c1', name: 'Franck Petit', email: 'f.petit@free.fr', phone: '+33690807060', status: 'contacted', assignedTo: 'u5', createdAt: '2024-03-21' },
];

export const MOCK_CALL_STATS: CallStat[] = [
  { userId: 'u3', period: 'day', callCount: 12, avgDuration: 145, totalDuration: 1740 },
  { userId: 'u3', period: 'week', callCount: 58, avgDuration: 132, totalDuration: 7656 },
  { userId: 'u3', period: 'month', callCount: 245, avgDuration: 128, totalDuration: 31360 },
  { userId: 'u4', period: 'day', callCount: 25, avgDuration: 210, totalDuration: 5250 },
];

export const PLAN_LIMITS = {
  [SubscriptionPlan.BASIC]: 3,
  [SubscriptionPlan.PRO]: 15,
  [SubscriptionPlan.ENTERPRISE]: 100,
};
